import axios from 'axios';
import logger from '../lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// API Service for GoodHabits Family e-commerce
export const api = {
  // Categories
  getCategories: async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      return response.data;
    } catch (error) {
      logger.error('Error fetching categories:', error);
      throw error;
    }
  },

  getCategory: async (slug) => {
    try {
      const response = await axios.get(`${API}/categories/${slug}`);
      return response.data;
    } catch (error) {
      logger.error(`Error fetching category ${slug}:`, error);
      throw error;
    }
  },

  // Products
  getProducts: async (params = {}) => {
    try {
      const response = await axios.get(`${API}/products`, { params });
      return response.data;
    } catch (error) {
      logger.error('Error fetching products:', error);
      throw error;
    }
  },

  // Homepage CMS (Phase 4) — published content for the storefront
  getHomepage: async () => {
    try {
      const response = await axios.get(`${API}/homepage`);
      return response.data;
    } catch (error) {
      logger.error('Error fetching homepage:', error);
      return null;
    }
  },


  getFeaturedProducts: async () => {
    try {
      const response = await axios.get(`${API}/products/featured`);
      return response.data.products;
    } catch (error) {
      logger.error('Error fetching featured products:', error);
      throw error;
    }
  },

  getProduct: async (slug) => {
    try {
      const response = await axios.get(`${API}/products/${slug}`);
      return response.data;
    } catch (error) {
      logger.error(`Error fetching product ${slug}:`, error);
      throw error;
    }
  },

  // Orders
  createOrder: async (orderData) => {
    try {
      const response = await axios.post(`${API}/orders`, orderData);
      return response.data;
    } catch (error) {
      logger.error('Error creating order:', error);
      throw error;
    }
  },

  getOrder: async (orderId, sessionId = null) => {
    try {
      // SEC-003: ownership proof required — pass the Stripe session_id
      // from the /order-success?session_id=… URL.
      const params = sessionId ? { session_id: sessionId } : undefined;
      const response = await axios.get(`${API}/orders/${orderId}`, { params });
      return response.data;
    } catch (error) {
      logger.error(`Error fetching order ${orderId}:`, error);
      throw error;
    }
  },

  completeOrder: async (orderId, paymentId) => {
    try {
      const response = await axios.post(`${API}/orders/${orderId}/complete`, null, {
        params: { payment_id: paymentId }
      });
      return response.data;
    } catch (error) {
      logger.error(`Error completing order ${orderId}:`, error);
      throw error;
    }
  },

  // Stripe Payments
  createCheckoutSession: async (orderId) => {
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/payments/create-checkout`, {
        order_id: orderId,
        origin_url: originUrl
      });
      return response.data;
    } catch (error) {
      logger.error('Error creating checkout session:', error);
      throw error;
    }
  },

  getPaymentStatus: async (sessionId) => {
    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      return response.data;
    } catch (error) {
      logger.error('Error getting payment status:', error);
      throw error;
    }
  },

  getOrderDownloads: async (orderId, sessionId = null) => {
    try {
      // SEC-003: ownership proof required.
      const params = sessionId ? { session_id: sessionId } : undefined;
      const response = await axios.get(`${API}/orders/${orderId}/downloads`, { params });
      return response.data;
    } catch (error) {
      logger.error('Error getting order downloads:', error);
      throw error;
    }
  },

  // Settings (public)
  getSettings: async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      return response.data;
    } catch (error) {
      logger.error('Error getting settings:', error);
      throw error;
    }
  },

  // Discount codes
  validateDiscountCode: async (code, subtotal) => {
    try {
      const response = await axios.post(`${API}/discounts/validate`, { code, subtotal });
      return response.data;
    } catch (error) {
      logger.error('Error validating discount code:', error);
      throw error;
    }
  },

  // Reviews (public)
  getProductReviews: async (productId) => {
    try {
      const response = await axios.get(`${API}/reviews/product/${productId}`);
      return response.data;
    } catch (error) {
      logger.error('Error fetching reviews:', error);
      throw error;
    }
  },

  // Bundle pricing
  calculateBundlePrice: async (productIds) => {
    try {
      const response = await axios.post(`${API}/products/bundle-price`, { productIds });
      return response.data;
    } catch (error) {
      logger.error('Error calculating bundle price:', error);
      throw error;
    }
  },

  // Contact form
  submitContactForm: async (data) => {
    try {
      const response = await axios.post(`${API}/contact`, data);
      return response.data;
    } catch (error) {
      logger.error('Error submitting contact form:', error);
      throw error;
    }
  },

  // Newsletter subscription (used by Free Resources page, footer, newsletter page)
  subscribeNewsletter: async (email, opts = {}) => {
    try {
      const payload = {
        email,
        source: opts.source || 'website',
        lead_magnet: opts.lead_magnet || null,
      };
      const response = await axios.post(`${API}/newsletter/subscribe`, payload);
      return response.data;
    } catch (error) {
      logger.error('Error subscribing to newsletter:', error);
      throw error;
    }
  }
};

export default api;
